import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  linkWithCredential,
  signOut,
  onAuthStateChanged,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  multiFactor,
  sendEmailVerification,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { getDatabase, ref, onValue, get as rtdbGet, update as rtdbUpdate } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { lastSeenMs } from '../utils/onlineCounts';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

const app = initializeApp(firebaseConfig);

// --- App Check --------------------------------------------------------------
// Cloud Functions enforce App Check (enforceAppCheck: true) — callables reject
// requests without a valid attestation token. Admin is a web-only console, so
// reCAPTCHA Enterprise via the Firebase JS SDK. Same web app id / key as the
// driver + passenger web apps.
try {
  const appCheckKey = process.env.REACT_APP_FIREBASE_APPCHECK_KEY;
  const appCheckDebugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
  if (appCheckDebugToken && typeof self !== 'undefined') {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken === 'true' ? true : appCheckDebugToken;
  }
  if (appCheckKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckKey),
      isTokenAutoRefreshEnabled: true,
    });
  } else {
    console.warn('App Check: REACT_APP_FIREBASE_APPCHECK_KEY not set — callables will be rejected while enforcement is on.');
  }
} catch (e) {
  console.error('App Check init failed:', e?.message || e);
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const rtdb = getDatabase(app);
export const functions = getFunctions(app, 'asia-south1');
export const storage = getStorage(app);

export const listenMapAdCampaigns = (onData, onError) => onSnapshot(
  collection(db, 'mapAdCampaigns'),
  (snapshot) => onData(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
  onError,
);

export const listenMapAdRuntime = (onData, onError) => onSnapshot(
  doc(db, 'mapAdRuntime', 'control'),
  (snapshot) => onData(snapshot.exists() ? snapshot.data() : { enabled: false }),
  onError,
);

const mapAdCall = async (name, data) => (await httpsCallable(functions, name)(data)).data;
export const saveMapAdDraft = (data) => mapAdCall('saveMapAdDraft', data);
export const publishMapAd = (data) => mapAdCall('publishMapAd', data);
export const setMapAdCampaignState = (data) => mapAdCall('setMapAdCampaignState', data);
export const setMapAdsEnabled = (enabled) => mapAdCall('setMapAdsEnabled', { enabled });

export const uploadMapAdImage = async (campaignId, placement, file) => {
  const extension = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : null;
  if (!extension || !['top', 'tag'].includes(placement)) throw new Error('Use a PNG or WebP image');
  const max = placement === 'top' ? 350 * 1024 : 120 * 1024;
  if (file.size < 50) throw new Error(`${placement} image is empty or too small`);
  if (file.size > max) throw new Error(`${placement} image exceeds the size limit`);
  const id = globalThis.crypto?.randomUUID?.()?.replaceAll('-', '')
    || Array.from(globalThis.crypto?.getRandomValues?.(new Uint8Array(16)) || [], (b) => b.toString(16).padStart(2, '0')).join('');
  if (!id) throw new Error('Secure upload ID unavailable');
  const path = `map-ads/${campaignId}/${placement}/${id}.${extension}`;
  await uploadBytes(storageRef(storage, path), file, { contentType: file.type, cacheControl: 'public,max-age=86400' });
  return path;
};

export const mapAdImageUrl = (path) => getDownloadURL(storageRef(storage, path));

// Mirrors DEFAULT_RINGS in firebase/functions/dispatch.js. Editing these here
// only pre-fills the Settings form; the server clamps every value it reads.
export const DEFAULT_DISPATCH = {
  rings: [
    { radiusM: 200, holdMs: 15000 },
    { radiusM: 700, holdMs: 15000 },
    { radiusM: 2000, holdMs: 15000 },
    { radiusM: null, holdMs: 15000 },
  ],
  briefDisconnectGraceSec: 90,
  fallbackMaxAgeSec: 1500,
  fallbackEnabled: true,
};

export const DEFAULT_PLATFORM = {
  city: 'Piler',
  state: 'Andhra Pradesh',
  mapCenterLat: 13.6549,
  mapCenterLng: 78.9471,
  serviceRadiusKm: 5,
  nearbyCaptainRadiusKm: 4,
  captainLocationMaxAgeSec: 180,
  supportPhone: '7989020222',
  supportWhatsapp: '917989020222',
  fareBase: 30,
  farePerKm: 15,
  fareMinimum: 30,
  mode: 'normal', // 'normal' | 'paused' | 'emergency'
  modeMessage: '',
  bookingEnabled: true, // derived mirror of mode, kept for the passenger app
  // A required update is intentionally opt-in and has no default minimum
  // version. Blank versions mean no app can be gated by accident.
  releaseNotice: {
    enabled: false,
    type: 'required_update',
    audience: 'all',
    title: '',
    message: '',
    passengerMinVersion: '',
    driverMinVersion: '',
  },
  // Extra towns beyond city/mapCenter/serviceRadiusKm above (which stays the
  // default zone — see rideSecurity.js resolveZone / passenger zones.js).
  // Same fare everywhere; a zone only ever changes WHERE someone can book.
  zones: [],
  dispatch: DEFAULT_DISPATCH,
  // Night operating window (IST). permitEnabled gates who may accept a ride;
  // pricingEnabled applies fareMultiplier on top of the normal fare. Both
  // default off, so an untouched config/platform doc changes nothing.
  nightPermitEnabled: false,
  nightPricingEnabled: false,
  nightStartHour: 22,
  nightEndHour: 6,
  nightFareMultiplier: 1.5,
  // Rain surcharge: a manual switch, not a weather API — turn it on when it is
  // actually raining, off when it stops. No time window; independent of
  // night. Defaults off, so an untouched config/platform doc changes nothing.
  rainModeEnabled: false,
  rainFareMultiplier: 1.5,
  // Pickup waiting charge for local rides. `off` = nothing happens; `quiet` =
  // record what WOULD have been charged on every ride but charge nobody (use it
  // first, to check the proof is reliable); `on` = charge riders who accepted the
  // terms. Whole numbers only: free minutes 0–15, ₹/min 0–10, cap ₹0–50.
  waitingMode: 'off',
  waitingFreeMinutes: 3,
  waitingPerMinute: 2,
  waitingMaxCharge: 30,
  // Fixed routes never consume Directions API requests. Custom map-picked
  // destinations are an opt-in add-on with their own tightly bounded limits.
  intertown: {
    enabled: false,
    oneWayEnabled: true,
    returnEnabled: true,
    perKm: 20,
    // Applies to both fixed and custom Intertown bookings. Local ride coverage
    // remains independent under serviceRadiusKm.
    pickupRadiusKm: 5,
    waiting: { unit: 'minute', rate: 2, freeMinutes: 30, maxMinutes: 480 },
    eligibleDriverIds: [],
    custom: {
      enabled: false,
      // Retained for saved-config compatibility; the top-level Intertown
      // pickupRadiusKm is the authoritative control for new bookings.
      pickupRadiusKm: 5,
      minDistanceKm: 5,
      maxDistanceKm: 120,
    },
    routes: [
      { id: 'pileru-madanapalle', fromName: 'Pileru', toName: 'Madanapalle', distanceKm: 42, durationMin: 70, destinationRadiusKm: 8, enabled: true },
      { id: 'pileru-tirupati', fromName: 'Pileru', toName: 'Tirupati', distanceKm: 54, durationMin: 95, destinationRadiusKm: 8, enabled: true },
    ],
  },
};

export const getAdminAuthErrorMessage = (error) => {
  const code = error?.code || '';
  const msg = error?.message || '';
  if (code.includes('requests-from-referer') || msg.includes('referer') || msg.includes('API_KEY_HTTP_REFERRER')) {
    return 'This domain is blocked by your Firebase API key referrers. Add https://mytownly-rides-admin.web.app/*';
  }
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Invalid email or password.';
  }
  if (code === 'auth/operation-not-allowed') {
    return 'Email/password sign-in is not enabled. Enable it in Firebase Auth → Sign-in method.';
  }
  if (code === 'auth/too-many-requests') return 'Too many attempts. Wait a few minutes.';
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection.';
  if (code === 'auth/invalid-verification-code' || code === 'auth/missing-code' || code === 'auth/invalid-payload') {
    return 'That code is wrong or expired. Use the current 6-digit code from your authenticator app.';
  }
  if (code === 'auth/totp-challenge-timeout') return 'The 2FA challenge timed out. Sign in again.';
  if (code === 'auth/unverified-email') return 'Verify your email before setting up 2FA.';
  if (code === 'auth/maximum-second-factor-count-exceeded') return 'This account already has a second factor enrolled.';
  return msg || 'Sign-in failed. Try again.';
};

export const adminLogin = (email, password) =>
  signInWithEmailAndPassword(auth, email.trim(), password);

export const getMfaResolver = (error) => {
  if (error?.code === 'auth/multi-factor-auth-required') {
    return getMultiFactorResolver(auth, error);
  }
  return null;
};

export const completeMfaTotpSignIn = async (resolver, hintUid, totpCode) => {
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hintUid, totpCode.trim());
  return resolver.resolveSignIn(assertion);
};

export const getEnrolledFactors = () => {
  const user = auth.currentUser;
  if (!user) return [];
  try {
    return multiFactor(user).enrolledFactors || [];
  } catch {
    return [];
  }
};

// --- TOTP (authenticator app) enrollment ----------------------------------
// generateSecret -> show QR / manual key -> user types the 6-digit code from
// their app -> assertionForEnrollment -> multiFactor().enroll().
export const startAdminTotpEnrollment = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to set up 2FA');
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const otpauthUrl = secret.generateQrCodeUrl(user.email || 'admin', 'MyTownly Rides Admin');
  return { secret, otpauthUrl, secretKey: secret.secretKey };
};

export const completeAdminTotpEnrollment = async (secret, code, label = 'Authenticator app') => {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to set up 2FA');
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
  await multiFactor(user).enroll(assertion, label);
};

// Firebase requires a verified email before a second factor can be enrolled.
export const sendAdminEmailVerification = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  await sendEmailVerification(user);
};

export const unenrollAdminMfa = async (factorUid) => {
  const user = auth.currentUser;
  if (!user) return;
  await multiFactor(user).unenroll(factorUid);
};

/**
 * MFA status for the current admin session:
 *  - enrolled:   the account has >= 1 second factor registered
 *  - sessionMfa: THIS sign-in actually completed a second factor
 *                (token claim firebase.sign_in_second_factor is set)
 * Admin writes require sessionMfa === true (enforced in Firestore rules and
 * every admin Cloud Function via assertCallerIsAdmin).
 */
export const getAdminMfaStatus = async () => {
  const user = auth.currentUser;
  if (!user) return { enrolled: false, sessionMfa: false };
  let enrolled = false;
  try {
    enrolled = (multiFactor(user).enrolledFactors || []).length > 0;
  } catch { /* */ }
  let sessionMfa = false;
  try {
    const res = await user.getIdTokenResult();
    sessionMfa = res?.claims?.firebase?.sign_in_second_factor != null;
  } catch { /* */ }
  return { enrolled, sessionMfa, emailVerified: !!user.emailVerified };
};

export const adminLogout = () => signOut(auth);

export const onAuthChange = (cb) => onAuthStateChanged(auth, cb);

export const checkIsAdmin = async (uid) => {
  if (!uid) return false;
  try {
    const token = await auth.currentUser?.getIdTokenResult();
    if (token?.claims?.admin === true) return true;
  } catch { /* ignore */ }
  const snap = await getDoc(doc(db, 'admins', uid));
  return snap.exists();
};

export const getAdminRole = async (uid) => {
  if (!uid) return 'invalid';
  try {
    const snap = await getDoc(doc(db, 'admins', uid));
    if (snap.exists()) {
      const role = snap.data()?.role;
      return ['super_admin', 'reviewer'].includes(role) ? role : 'invalid';
    }
  } catch { /* ignore */ }
  try {
    const token = await auth.currentUser?.getIdTokenResult();
    if (['super_admin', 'reviewer'].includes(token?.claims?.role)) return token.claims.role;
  } catch { /* ignore */ }
  return 'invalid';
};

export const ensureAdminClaims = async () => {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    // Always calls syncAdminClaims, even when the token's claims already
    // look fine: syncAdminClaims also (re)writes the RTDB adminRoles/{uid}
    // mirror the Live Map's driverLocations read depends on, which can
    // silently drift out of sync with a claim that looks otherwise valid.
    const fn = httpsCallable(functions, 'syncAdminClaims');
    await fn({});
    const token = await user.getIdTokenResult();
    if (token?.claims?.admin === true && token?.claims?.role) return true;
    await user.getIdToken(true);
    return true;
  } catch (e) {
    console.warn('ensureAdminClaims note:', e?.message);
    return false;
  }
};

export const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v.toMillis === 'function') return new Date(v.toMillis());
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') return isNaN(v) ? null : new Date(v);
  if (typeof v === 'string') {
    const parsed = new Date(v);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export const getDocTimestamp = (d) => {
  const t = d?.updatedAt || d?.completedAt || d?.createdAt || d?.lastActiveAt;
  const date = toDate(t);
  return date ? date.getTime() : 0;
};

const mapDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

const byCreatedDesc = (a, b) => {
  const ta = getDocTimestamp(a);
  const tb = getDocTimestamp(b);
  return tb - ta;
};

export const logAdminAction = async (action, meta = {}) => {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    await httpsCallable(functions, 'writeAdminAuditLog')({ action, meta });
  } catch (e) {
    console.warn('admin log failed', e);
  }
};

export const listenDrivers = (cb) =>
  onSnapshot(collection(db, 'drivers'), (snap) => {
    const raw = mapDocs(snap);
    const sorted = raw.sort((a, b) => {
      const ta = getDocTimestamp(a);
      const tb = getDocTimestamp(b);
      return tb - ta;
    });
    cb(sorted);
  }, (err) => {
    console.error('listenDrivers error:', err);
  });

export const listenUsers = (cb) =>
  onSnapshot(collection(db, 'users'), (snap) => {
    // Keep the complete passenger list available to the admin console. The
    // Passengers screen paginates its rendering, so this must not silently
    // discard older accounts after an arbitrary first 500.
    cb(mapDocs(snap).sort(byCreatedDesc));
  }, (err) => {
    console.error('listenUsers error:', err);
  });

/** One-shot count of rides still only searching for a driver. */
export const countSearchingRides = async () => {
  const snap = await getDocs(query(collection(db, 'rides'), where('status', '==', 'searching')));
  return snap.size;
};

export const listenRides = (cb) =>
  onSnapshot(collection(db, 'rides'), (snap) => {
    const raw = mapDocs(snap);
    // Sort by createdAt, matching the Time column rendered in Rides.js —
    // sorting by getDocTimestamp() (updatedAt/completedAt) instead makes a
    // recently-completed old ride jump to the top while still showing its
    // original (older) createdAt, so the list appears out of order.
    const sorted = raw.sort((a, b) => {
      const ta = toDate(a.createdAt)?.getTime() || 0;
      const tb = toDate(b.createdAt)?.getTime() || 0;
      return tb - ta;
    });
    // Financial totals must use the complete snapshot already downloaded.
    // Pagination of operational lists must be separate from financial totals.
    cb(sorted);
  }, (err) => {
    console.error('listenRides error:', err);
  });

export const listenAdminLogs = (cb) =>
  onSnapshot(
    query(collection(db, 'adminLogs'), orderBy('createdAt', 'desc'), limit(100)),
    (snap) => cb(mapDocs(snap)),
    () => onSnapshot(collection(db, 'adminLogs'), (snap) => {
      cb(mapDocs(snap).sort(byCreatedDesc).slice(0, 100));
    }),
  );

export const listenSosEvents = (cb) =>
  onSnapshot(
    query(collection(db, 'sosEvents'), orderBy('createdAt', 'desc'), limit(100)),
    (snap) => cb(mapDocs(snap)),
    () => onSnapshot(collection(db, 'sosEvents'), (snap) => {
      cb(mapDocs(snap).sort(byCreatedDesc).slice(0, 100));
    }),
  );

export const listenToTripTrails = (cb, onError) =>
  onSnapshot(
    query(collection(db, 'tripTrails'), orderBy('createdAt', 'desc'), limit(300)),
    (snap) => cb(mapDocs(snap)),
    (err) => {
      console.warn('listenToTripTrails fallback:', err?.message);
      onSnapshot(collection(db, 'tripTrails'), (snap) => {
        cb(mapDocs(snap).sort(byCreatedDesc).slice(0, 300));
      }, onError);
    },
  );

export const acknowledgeSos = async (sosId) => {
  await updateDoc(doc(db, 'sosEvents', sosId), {
    handlingStatus: 'acknowledged',
    acknowledgedAt: serverTimestamp(),
    acknowledgedBy: auth.currentUser?.uid || null,
    acknowledgedEmail: auth.currentUser?.email || '',
  });
  await logAdminAction('sos_ack', { sosId });
};

export const resolveSos = async (sosId, note = '') => {
  await updateDoc(doc(db, 'sosEvents', sosId), {
    handlingStatus: 'resolved',
    resolvedAt: serverTimestamp(),
    resolvedBy: auth.currentUser?.uid || null,
    resolveNote: String(note || '').slice(0, 300),
  });
  await logAdminAction('sos_resolve', { sosId });
};

export const listenPlatformConfig = (cb) =>
  onSnapshot(doc(db, 'config', 'platform'), (snap) => {
    cb(snap.exists() ? { ...DEFAULT_PLATFORM, ...snap.data() } : { ...DEFAULT_PLATFORM });
  });

export const savePlatformConfig = async (data) => {
  await setDoc(doc(db, 'config', 'platform'), {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.uid || null,
  }, { merge: true });
  await logAdminAction('settings_update', { keys: Object.keys(data) });
};

// Dispatch Engine tab. updateDoc on the single field REPLACES `dispatchV2` as a whole (a
// merge-write would leave deleted town overrides behind) and touches nothing else in
// config/platform, in particular never `dispatch` or `zones`. Super admin + MFA only (rules).
export const saveDispatchEngineConfig = async (dispatchV2) => {
  await updateDoc(doc(db, 'config', 'platform'), {
    dispatchV2,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.uid || null,
  });
  await logAdminAction('settings_update', { keys: ['dispatchV2'] });
};

// config/dispatchInternal holds the Intertown eligible-drivers list —
// deliberately separate from config/platform, which every signed-in
// passenger/driver reads directly for fare config, service area, etc.
// Firestore can't redact one field from an otherwise-public document, so
// this had to move to its own admin-only document (firestore.rules).
export const listenDispatchInternalConfig = (cb) =>
  onSnapshot(doc(db, 'config', 'dispatchInternal'), (snap) => {
    cb(snap.exists() ? snap.data() : null);
  });

export const saveEligibleDriverIds = async (ids) => {
  const eligibleDriverIds = Array.isArray(ids) ? ids : [];
  await setDoc(doc(db, 'config', 'dispatchInternal'), {
    eligibleDriverIds,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.uid || null,
  }, { merge: true });
  // Clear the legacy copy so it stops riding along in every passenger/
  // driver's own read of config/platform. {merge: true} + deleteField()
  // removes only this one nested field, leaving perKm/routes/etc. untouched.
  await setDoc(doc(db, 'config', 'platform'), {
    intertown: { eligibleDriverIds: deleteField() },
  }, { merge: true });
  await logAdminAction('settings_update', { keys: ['intertown.eligibleDriverIds'] });
};

// One-time, idempotent self-heal: copies the legacy eligible-drivers list
// out of config/platform (still readable there until this runs) into the
// admin-only config/dispatchInternal doc, then clears the old copy. Safe to
// call on every admin panel load — a no-op once dispatchInternal exists.
export const migrateEligibleDriverIdsIfNeeded = async (legacyIds) => {
  const dispatchInternalSnap = await getDoc(doc(db, 'config', 'dispatchInternal'));
  if (dispatchInternalSnap.exists()) return;
  if (!Array.isArray(legacyIds) || !legacyIds.length) return;
  await saveEligibleDriverIds(legacyIds);
};

export const listenToIntertownRequests = (callback) => onSnapshot(
  query(collection(db, 'intertownRequests'), orderBy('createdAt', 'desc'), limit(80)),
  (snap) => callback(snap.docs.map((item) => ({ id: item.id, ...item.data() }))),
  (error) => { console.error('intertown requests listener:', error?.message); callback([]); },
);

// Recovery must never depend on the finite history feed above. Keep a separate
// listener for every live state so an older stuck trip remains visible to the
// force-complete tool even after hundreds of newer requests arrive.
const INTERTOWN_ACTIVE_STATUSES = [
  'pending_admin_confirmation', 'open_for_claim', 'awaiting_driver_acceptance',
  'confirmed', 'arrived_at_pickup', 'started', 'at_destination', 'returning',
];
export const listenToActiveIntertownRequests = (callback) => onSnapshot(
  query(
    collection(db, 'intertownRequests'),
    where('status', 'in', INTERTOWN_ACTIVE_STATUSES),
  ),
  (snap) => callback(snap.docs.map((item) => ({ id: item.id, ...item.data() }))),
  (error) => { console.error('active intertown requests listener:', error?.message); callback([]); },
);

// Server-written operational history (opened/reopened/claimed/declined/
// manual_assigned/expired/notification_sent) — firestore.rules gates this
// subcollection on isAdminMfa() regardless of what this client requests, so
// a reviewer without the second factor simply gets a permission-denied here.
export const listenToIntertownAuditLog = (requestId, callback, onError) => {
  if (!requestId) return () => {};
  return onSnapshot(
    query(collection(db, 'intertownRequests', requestId, 'auditLog'), orderBy('at', 'asc'), limit(100)),
    (snap) => callback(snap.docs.map((item) => ({ id: item.id, ...item.data() }))),
    (error) => {
      console.warn('intertown audit log listener:', error?.message);
      // Distinct from "no entries yet" — the caller needs to tell an admin
      // "you don't have access" apart from "nothing has happened here",
      // otherwise a reviewer without the 2FA claim silently sees an empty
      // timeline and may read that as proof nothing happened.
      onError?.(error);
      callback([]);
    },
  );
};

export const assignIntertownRequest = async (requestId, driverId) => (
  await httpsCallable(functions, 'assignIntertownRequest')({ requestId, driverId })
).data;

export const cancelIntertownRequest = async (requestId, reason = 'Cancelled by admin') => (
  await httpsCallable(functions, 'cancelIntertownRequest')({ requestId, reason, admin: true })
).data;

export const forceCompleteIntertownRequest = async (requestId, reason) => (
  await httpsCallable(functions, 'forceCompleteIntertownRequest')({ requestId, reason })
).data;

export const listenDriverLocations = (cb) => {
  let isCancelled = false;
  let rtdbUnsub = null;
  let pollingInterval = null;
  let isPolling = false;

  const parseRtdbSnapshot = (snap) => {
    const val = snap.exists() ? snap.val() : {};
    const now = Date.now();
    return Object.entries(val).map(([id, loc]) => {
      // "Last heard from": the database server's clock (lastSeen) when the
      // Anna build sends it, else the phone's own updatedAt — the same rule the
      // server uses (firebase/functions/presence.js), so a phone with a wrong
      // clock doesn't show as reconnecting, or as live after it has died.
      const lastSeen = Number(loc?.lastSeen);
      const seenAt = Number.isFinite(lastSeen) && lastSeen > 0 ? lastSeen : (Number(loc?.updatedAt) || 0);
      return {
        id,
        lat: typeof loc?.lat === 'number' ? loc.lat : null,
        lng: typeof loc?.lng === 'number' ? loc.lng : null,
        heading: Number(loc?.heading) || 0,
        updatedAt: seenAt,
        stale: !seenAt || now - seenAt > 180000,
      };
    }).filter((d) => d.lat != null && d.lng != null);
  };

  const startPollingFallback = () => {
    if (isPolling || isCancelled) return;
    isPolling = true;
    const fetchSnapshot = async () => {
      if (isCancelled) return;
      try {
        const fn = httpsCallable(functions, 'getFleetLocationsForAdmin');
        const res = await fn({});
        if (!isCancelled && Array.isArray(res.data?.drivers)) {
          cb(res.data.drivers);
        }
      } catch (err) {
        console.warn('Fleet locations polling fallback warning:', err?.message);
      }
    };
    fetchSnapshot();
    // Degraded fallback only — reached when the RTDB socket is down. Each tick is
    // a getFleetLocationsForAdmin call + a full driverLocations node read, so a
    // left-open Live Map on a flaky connection was burning ~15 invocations/min.
    // 30s is plenty for an internal "where are the autos" view; the moment the
    // socket recovers, onValue fires and stopPollingFallback() kills this.
    pollingInterval = setInterval(fetchSnapshot, 30000);
  };

  const stopPollingFallback = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
    isPolling = false;
  };

  const attachRtdbListener = () => {
    if (isCancelled) return;
    try {
      const locsRef = ref(rtdb, 'driverLocations');
      rtdbUnsub = onValue(locsRef, (snap) => {
        if (isCancelled) return;
        stopPollingFallback();
        const list = parseRtdbSnapshot(snap);
        cb(list);
      }, (err) => {
        console.warn('RTDB driverLocations socket disconnected/failed, engaging self-healing polling:', err?.message);
        startPollingFallback();
      });
    } catch (e) {
      console.warn('Failed to attach RTDB socket, falling back to polling:', e?.message);
      startPollingFallback();
    }
  };

  attachRtdbListener();

  return () => {
    isCancelled = true;
    stopPollingFallback();
    if (typeof rtdbUnsub === 'function') {
      try { rtdbUnsub(); } catch { /* ignore */ }
    }
  };
};

/**
 * One-shot read of when each Anna's GPS row was last written (id -> ms), for the header's
 * "live" count. Deliberately NOT a listener: driverLocations changes about twice a second,
 * and streaming it would download every tick to every open admin tab all day. The header
 * reads it once a minute instead (App.js).
 */
export const fetchDriverLastSeen = async () => {
  const snap = await rtdbGet(ref(rtdb, 'driverLocations'));
  const val = snap.exists() ? snap.val() : {};
  const lastSeenById = {};
  Object.entries(val || {}).forEach(([id, loc]) => { lastSeenById[id] = lastSeenMs(loc); });
  return lastSeenById;
};

export const listenRideMessages = (rideId, cb) => {
  if (!rideId) return () => {};
  return onSnapshot(
    query(collection(db, 'rides', rideId, 'messages'), orderBy('createdAt', 'asc'), limit(100)),
    (snap) => cb(mapDocs(snap)),
    () => onSnapshot(collection(db, 'rides', rideId, 'messages'), (snap) => {
      cb(mapDocs(snap).sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return ta - tb;
      }));
    }),
  );
};

export const listenRideEvents = (rideId, cb) => {
  if (!rideId) return () => {};
  return onSnapshot(
    query(collection(db, 'rides', rideId, 'events'), orderBy('at', 'desc'), limit(100)),
    (snap) => cb(mapDocs(snap)),
    () => onSnapshot(collection(db, 'rides', rideId, 'events'), (snap) => {
      cb(mapDocs(snap).sort((a, b) => {
        const ta = a.at?.toMillis?.() || 0;
        const tb = b.at?.toMillis?.() || 0;
        return tb - ta;
      }));
    }),
  );
};

/**
 * Server-written ride history (who accepted, who cancelled and why, rebroadcasts,
 * dispatch rings). See firebase/functions/rideTimeline.js. Oldest first.
 */
export const listenRideTimeline = (rideId, cb) => {
  if (!rideId) return () => {};
  return onSnapshot(
    query(collection(db, 'rides', rideId, 'timeline'), orderBy('at', 'asc'), limit(300)),
    (snap) => cb(mapDocs(snap)),
    () => onSnapshot(collection(db, 'rides', rideId, 'timeline'), (snap) => {
      cb(mapDocs(snap).sort((a, b) => {
        const ta = a.at?.toMillis?.() || 0;
        const tb = b.at?.toMillis?.() || 0;
        return ta - tb;
      }));
    }),
  );
};

export const listenAllRideEvents = (cb) => {
  return onSnapshot(
    query(collectionGroup(db, 'events'), orderBy('at', 'desc'), limit(200)),
    (snap) => {
      const docs = snap.docs.map((d) => {
        const rideId = d.ref.parent.parent?.id || '';
        return { id: d.id, rideId, ...d.data() };
      });
      cb(docs);
    },
    () => onSnapshot(collectionGroup(db, 'events'), (snap) => {
      const docs = snap.docs.map((d) => {
        const rideId = d.ref.parent.parent?.id || '';
        return { id: d.id, rideId, ...d.data() };
      }).sort((a, b) => {
        const ta = a.at?.toMillis?.() || 0;
        const tb = b.at?.toMillis?.() || 0;
        return tb - ta;
      }).slice(0, 200);
      cb(docs);
    }),
  );
};

export const setDriverApproved = async (uid, isApproved) => {
  const patch = {
    isApproved: !!isApproved,
    updatedAt: serverTimestamp(),
  };
  // On approve, promote any pending photo replacements to live.
  if (isApproved) {
    const snap = await getDoc(doc(db, 'drivers', uid));
    const d = snap.exists() ? snap.data() : {};
    if (d.pendingPhotoUrl) {
      patch.photoUrl = d.pendingPhotoUrl;
      patch.pendingPhotoUrl = null;
    }
    if (d.pendingVehiclePhotoUrl) {
      patch.vehiclePhotoUrl = d.pendingVehiclePhotoUrl;
      patch.pendingVehiclePhotoUrl = null;
    }
    patch.photosPendingReview = false;
  }
  await updateDoc(doc(db, 'drivers', uid), patch);
  await logAdminAction(isApproved ? 'driver_approve' : 'driver_unapprove', { driverId: uid });
};

/** Apply pending captain/auto photos without changing approval status. */
export const approveDriverPendingPhotos = async (uid) => {
  const snap = await getDoc(doc(db, 'drivers', uid));
  if (!snap.exists()) throw new Error('missing_driver');
  const d = snap.data();
  const patch = {
    photosPendingReview: false,
    updatedAt: serverTimestamp(),
  };
  if (d.pendingPhotoUrl) {
    patch.photoUrl = d.pendingPhotoUrl;
    patch.pendingPhotoUrl = null;
  }
  if (d.pendingVehiclePhotoUrl) {
    patch.vehiclePhotoUrl = d.pendingVehiclePhotoUrl;
    patch.pendingVehiclePhotoUrl = null;
  }
  await updateDoc(doc(db, 'drivers', uid), patch);
  await logAdminAction('driver_photos_approve', { driverId: uid });
};

/** Discard pending captain/auto photos — keep the live ones. */
export const rejectDriverPendingPhotos = async (uid) => {
  await updateDoc(doc(db, 'drivers', uid), {
    pendingPhotoUrl: null,
    pendingVehiclePhotoUrl: null,
    photosPendingReview: false,
    updatedAt: serverTimestamp(),
  });
  await logAdminAction('driver_photos_reject', { driverId: uid });
};

export const setDriverBlocked = async (uid, isBlocked) => {
  const patch = {
    isBlocked: !!isBlocked,
    updatedAt: serverTimestamp(),
  };
  if (isBlocked) patch.isOnline = false;
  await updateDoc(doc(db, 'drivers', uid), patch);
  await logAdminAction(isBlocked ? 'driver_block' : 'driver_unblock', { driverId: uid });
};

/** Whether this Anna may accept rides during the admin's configured night
 * window (config/platform.nightPermitEnabled + hours) — independent of
 * whether that window is currently switched on. */
export const setDriverNightPermit = async (uid, nightPermit) => {
  await updateDoc(doc(db, 'drivers', uid), {
    nightPermit: !!nightPermit,
    updatedAt: serverTimestamp(),
  });
  await logAdminAction(nightPermit ? 'driver_night_permit_grant' : 'driver_night_permit_revoke', { driverId: uid });
};

/** Admin edit: name, phone, vehicle plate, and optional notes. */
export const updateDriverProfile = async (uid, fields = {}) => {
  if (!uid) throw new Error('missing_driver');
  const patch = { updatedAt: serverTimestamp() };
  if (fields.name !== undefined) {
    const name = String(fields.name || '').trim().slice(0, 60);
    if (!name) throw new Error('name_required');
    patch.name = name;
  }
  if (fields.phone !== undefined) {
    const digits = String(fields.phone || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) throw new Error('phone_invalid');
    patch.phone = `+91${digits}`;
  }
  if (fields.vehicleNumber !== undefined) {
    const vehicle = String(fields.vehicleNumber || '').trim().toUpperCase().replace(/\s+/g, ' ').slice(0, 32);
    if (vehicle.length < 4) throw new Error('vehicle_invalid');
    patch.vehicleNumber = vehicle;
  }
  if (fields.adminNote !== undefined) {
    patch.adminNote = String(fields.adminNote || '').trim().slice(0, 300);
  }
  await updateDoc(doc(db, 'drivers', uid), patch);
  await logAdminAction('driver_edit', {
    driverId: uid,
    fields: Object.keys(fields),
  });
};

export const updateDriverDocumentStatus = async (uid, docKey, status, notes = '') => {
  const driverRef = doc(db, 'drivers', uid);
  const patch = {
    [`documents.${docKey}.status`]: status,
    [`documents.${docKey}.rejectionReason`]: status === 'rejected' ? String(notes || '').trim() : null,
    [`documents.${docKey}.verifiedAt`]: status === 'verified' ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  };
  await updateDoc(driverRef, patch);
  await logAdminAction('driver_doc_status', { driverId: uid, docKey, status, notes });
};

export const updateDriverVehicleSafety = async (uid, safety) => {
  if (!uid || !safety) throw new Error('vehicle_safety_invalid');
  const clean = {};
  ['rc', 'insurance', 'drivingLicence'].forEach((key) => {
    const item = safety[key] || {};
    clean[key] = {
      status: ['valid', 'pending', 'expired', 'unavailable'].includes(item.status)
        ? item.status
        : 'unavailable',
      validUpto: String(item.validUpto || '').slice(0, 20),
    };
  });
  const validCount = Object.values(clean).filter((item) => item.status === 'valid').length;
  await updateDoc(doc(db, 'drivers', uid), {
    vehicleSafety: {
      ...clean,
      published: Boolean(safety.published) && validCount >= 3,
      verifiedAt: serverTimestamp(),
      verifiedBy: auth.currentUser?.uid || null,
    },
    updatedAt: serverTimestamp(),
  });
  await logAdminAction('driver_vehicle_safety_update', {
    driverId: uid,
    published: Boolean(safety.published) && validCount >= 3,
    validCount,
  });
};

export const setUserBlocked = async (uid, isBlocked) => {
  await updateDoc(doc(db, 'users', uid), {
    isBlocked: !!isBlocked,
    updatedAt: serverTimestamp(),
  });
  await logAdminAction(isBlocked ? 'user_block' : 'user_unblock', { userId: uid });
};

export const unflagPassengerForPrank = async (uid) => {
  await updateDoc(doc(db, 'users', uid), {
    isFlaggedForPrank: false,
    consecutiveCancellations: 0,
    unflaggedAt: serverTimestamp(),
    unflaggedBy: auth.currentUser?.uid || null,
    updatedAt: serverTimestamp(),
  });
  await logAdminAction('user_unflag_prank', { userId: uid });
};

/**
 * Hard-delete passenger or captain (Auth + Firestore profile). Ride history kept.
 * @param {'passenger'|'driver'} role
 */
export const adminDeleteAccount = async (uid, role, confirmPhone) => {
  const fn = httpsCallable(functions, 'adminDeleteAccount');
  const res = await fn({ uid, role, confirmPhone });
  return res.data;
};

export const cancelRideAsAdmin = async (rideId, reason = 'Cancelled by admin') => {
  await updateDoc(doc(db, 'rides', rideId), {
    status: 'cancelled',
    // Recorded so the server never counts an admin cancel as the rider's late
    // cancel (fee ladder) and the rider's push shows the admin's reason.
    cancelledBy: 'admin',
    cancelReason: reason,
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await logAdminAction('ride_cancel', { rideId, reason });
};

export const completeRideAsAdmin = async (rideId) => {
  return (await httpsCallable(functions, 'completeRideAsAdmin')({ rideId })).data;
};

/**
 * Each online driver's town, resolved live from their current GPS the same
 * way dispatch itself decides which town a driver belongs to — never a
 * stored field, so it can't go stale. zoneId/zoneName are null for a driver
 * whose last location falls outside every configured zone.
 */
export const getFleetZonesForAdmin = async () => {
  const fn = httpsCallable(functions, 'getFleetLocationsForAdmin');
  const res = await fn({});
  return Array.isArray(res.data?.drivers) ? res.data.drivers : [];
};

/**
 * Server-authoritative Anna dispatch control. `suspend` stops new offers now
 * when idle, or after the current trip when active. `clear` never forces an
 * Anna online; their phone must opt in with a valid location service.
 */
export const setDriverDispatchControl = async (driverId, action) => {
  return (await httpsCallable(functions, 'setDriverDispatchControl')({ driverId, action })).data;
};

/**
 * Dues hold (super admin): action 'hold' or 'release'. A held Anna gets no new rides
 * or trips and can't go online until he has paid, but keeps full use of the app so he
 * can pay. Not Block, which locks the whole app. A recorded payment that clears his
 * dues lifts the hold by itself. The server decides; see setDriverDuesHold.
 */
export const setDriverDuesHold = async (driverId, action) => {
  return (await httpsCallable(functions, 'setDriverDuesHold')({ driverId, action })).data;
};

/**
 * Put ride back on the market for captains (clear assignment). Server-side:
 * a raw client updateDoc here previously left notifiedDriverIds untouched,
 * so the just-unassigned Anna could still pass acceptRideSecure's own
 * eligibility check and re-accept the exact ride she was just pulled off.
 */
export const rebroadcastRideAsAdmin = async (rideId) => {
  return (await httpsCallable(functions, 'rebroadcastRideAsAdmin')({ rideId })).data;
};

/** Checklist before approving a captain. */
export const getDriverChecklist = (d) => {
  const phoneDigits = String(d?.phone || '').replace(/\D/g, '');
  const phoneOk = phoneDigits.slice(-10).length === 10;
  const vehicleOk = String(d?.vehicleNumber || '').trim().length >= 4;
  const photo = d?.photoUrl || d?.pendingPhotoUrl || d?.driverPhoto || d?.profilePhoto;
  const photoOk = Boolean(
    photo && (String(photo).startsWith('http') || String(photo).startsWith('data:')),
  );
  const vPhoto = d?.vehiclePhotoUrl || d?.rcPhotoUrl || d?.pendingVehiclePhotoUrl || d?.vehiclePhoto;
  const vehiclePhotoOk = Boolean(
    vPhoto && (String(vPhoto).startsWith('http') || String(vPhoto).startsWith('data:')),
  );
  const missing = [];
  if (!photoOk) missing.push('photo');
  if (!vehiclePhotoOk) missing.push('auto photo');
  if (!vehicleOk) missing.push('vehicle number');
  if (!phoneOk) missing.push('valid phone');
  return {
    phoneOk,
    vehicleOk,
    photoOk,
    vehiclePhotoOk,
    ready: phoneOk && vehicleOk && photoOk && vehiclePhotoOk,
    missing,
  };
};

export const formatTime = (v) => {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '').slice(-10);
  return digits.length === 10 ? `+91 ${digits}` : (phone || '—');
};

export const phoneHref = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  const local = digits.length === 10 ? `91${digits}` : digits;
  return `tel:+${local}`;
};

export const waHref = (phone, text = '') => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  const local = digits.length === 10 ? `91${digits}` : digits;
  const q = text ? `?text=${encodeURIComponent(text)}` : '';
  return `https://wa.me/${local}${q}`;
};

export const createManualDispatchRide = async (data) => {
  const fn = httpsCallable(functions, 'manualDispatchRide');
  const res = await fn(data);
  return res.data;
};

export const sendAdminBroadcast = async (data) => {
  const bRef = await addDoc(collection(db, 'broadcasts'), {
    target: data.target || 'drivers',
    title: data.title || 'Notification',
    body: data.body || '',
    language: data.language || 'te',
    createdBy: auth.currentUser?.uid || 'admin',
    createdAt: serverTimestamp(),
  });
  await logAdminAction('send_broadcast', { broadcastId: bRef.id, target: data.target });
  return bRef.id;
};

export const listenToBroadcasts = (callback) => {
  const q = query(collection(db, 'broadcasts'), orderBy('createdAt', 'desc'), limit(15));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(list);
  }, (err) => {
    console.error('listenToBroadcasts error:', err);
  });
};

export const clearDriverPendingDues = async (payment) =>
  (await httpsCallable(functions, 'recordDriverPayment')(payment)).data;

export const listenDriverPayments = (cb) => {
  const q = query(collection(db, 'driverPayments'), orderBy('paidAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    cb(mapDocs(snap));
  }, (err) => {
    console.error('listenDriverPayments error:', err);
    cb([]);
  });
};

export const reconcileDriverAccounting = async (driverId) =>
  (await httpsCallable(functions, 'reconcileDriverAccounting')({ driverId })).data;

/** One-time, server-authoritative pre-launch operational-data reset. */
export const getLaunchResetPreview = async () =>
  (await httpsCallable(functions, 'launchDataReset')({ action: 'preview' })).data;

export const executeLaunchReset = async (confirmation) =>
  (await httpsCallable(functions, 'launchDataReset')({ action: 'execute', confirmation })).data;

export const listenDeletionRequests = (cb) =>
  onSnapshot(
    query(collection(db, 'deletionRequests'), orderBy('createdAt', 'desc'), limit(100)),
    (snap) => cb(mapDocs(snap)),
    () => cb([]),
  );

export const processDeletionRequest = async (requestId, action = 'fulfill') => {
  const fn = httpsCallable(functions, 'processDeletionRequest');
  const result = await fn({ requestId, action });
  return result.data;
};

export const askAdminAi = async (prompt) => {
  const fn = httpsCallable(functions, 'adminAiQuery');
  const result = await fn({ mode: 'query', prompt });
  return result.data;
};

export const generateAiTeluguBroadcast = async (prompt) => {
  const fn = httpsCallable(functions, 'adminAiQuery');
  const result = await fn({ mode: 'broadcast', prompt });
  return result.data;
};

export const auditUserRiskAi = async (targetData) => {
  const fn = httpsCallable(functions, 'adminAiQuery');
  const result = await fn({ mode: 'risk_audit', targetData });
  return result.data;
};
